import { Button, EmptyState, ErrorState, Skeleton } from "@helmr/ui";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { isApiError } from "../../api/client";
import { listStaff, type StaffMember } from "../../api/staff";
import { NoAccess } from "../../app/NoAccess";
import { useAppContext, useSessionSnapshot } from "../../app/permissions";
import { useServices } from "../../app/services";
import { today as todayOf } from "../../lib/dates";
import s from "./common.module.css";

/** Business, branch and filter in every query key, so a cached list can never be read in another context (tenant guard). */
export function useScope() {
  const { now } = useServices();
  const ctx = useAppContext();
  const snap = useSessionSnapshot();
  const date = todayOf(now(), ctx.timeZone);
  const branchId = snap.branchFilter === "all" ? undefined : snap.branchFilter;
  return { ctx, date, branchId, nowMs: now(), key: [snap.businessId, snap.branchId, snap.branchFilter] as const };
}
export type Scope = ReturnType<typeof useScope>;

/** True from 768px. jsdom has no `matchMedia`, so tests see the phone layout unless they stub it. */
export function useIsWide(): boolean {
  const q = "(min-width: 768px)";
  const get = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(q).matches : false);
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(q);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
}

/** Trainer and staff names. `GET /staff` needs `staff:view`; without it only the caller's own name is known, and any other
 * trainer reads "Trainer ·<last 4 of id>" so columns stay distinguishable (a trainer directory needs an API change). */
export function useStaffNames() {
  const { client } = useServices();
  const sc = useScope();
  // GET /staff checks staff.view; names move to GET /directory in roles-directory-names.
  const enabled = sc.ctx.has("staff.view");
  const q = useQuery({ queryKey: ["staff", ...sc.key, "directory"], queryFn: () => listStaff(client, { status: "active" }), enabled, retry: false });
  const list: StaffMember[] = q.data?.staff ?? [];
  const names = new Map(list.map((m) => [m.id, m.name]));
  if (sc.ctx.me && !names.has(sc.ctx.me.staff.id)) names.set(sc.ctx.me.staff.id, sc.ctx.me.staff.name);
  return { names, list, enabled, nameOf: (id: string | null | undefined) => (id ? names.get(id) ?? `Trainer ·${id.slice(-4)}` : "No coach") };
}

export const messageOf = (e: unknown) => (isApiError(e) ? e.message : e instanceof Error ? e.message : "Something went wrong. Try again.");

export function Failure({ error, title, onRetry }: { error: unknown; title: string; onRetry: () => void }) {
  if (isApiError(error) && error.status === 403) return <NoAccess compact />;
  return <ErrorState compact title={title} message={messageOf(error)} requestId={isApiError(error) ? error.requestId : null} onRetry={onRetry} />;
}

export function Gate<T>({ query, skeleton, title, children }: { query: Pick<UseQueryResult<T>, "data" | "error" | "isPending" | "refetch">; skeleton: ReactNode; title: string; children: (d: T) => ReactNode }) {
  if (query.isPending) return <>{skeleton}</>;
  if (query.error) return <Failure error={query.error} title={title} onRetry={() => void query.refetch()} />;
  return <>{children(query.data as T)}</>;
}

export const RowsSkeleton = ({ rows = 5 }: { rows?: number }) => (
  <div aria-busy="true" aria-label="Loading">{Array.from({ length: rows }, (_, i) => <div className={s.skRow} key={i}><Skeleton width="45%" height={13} /><Skeleton width="70%" height={12} /></div>)}</div>
);

export function Field({ label, htmlFor, hint, children }: { label: string; htmlFor?: string; hint?: string; children: ReactNode }) {
  return <div className={s.field}><label htmlFor={htmlFor}>{label}</label>{children}{hint ? <small>{hint}</small> : null}</div>;
}

export const FormError = ({ message }: { message: string | null }) => (message ? <p className={s.err} role="alert">{message}</p> : null);

/** A destructive action asks once, in place. */
export function Confirm({ label, question, onConfirm, busy, variant = "default" }: { label: string; question: string; onConfirm: () => void; busy?: boolean; variant?: "default" | "primary" }) {
  const [asking, setAsking] = useState(false);
  if (!asking) return <Button variant={variant} onClick={() => setAsking(true)}>{label}</Button>;
  return (
    <div className={s.row} role="group" aria-label={question}>
      <span className={s.note}>{question}</span>
      <Button variant="primary" busy={busy} busyLabel="Working…" onClick={onConfirm}>{`Yes, ${label.toLowerCase()}`}</Button>
      <Button variant="plain" onClick={() => setAsking(false)}>Keep</Button>
    </div>
  );
}

export { EmptyState, s as styles };
